import React, { useState } from 'react';
import {
  Form,
  redirect,
  useNavigation,
  useNavigate,
  Link,
  useSearchParams,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import customFetch from '../utils/customFetch';
import { persistLoginSession } from '../utils/persistLoginSession';
import { safeRedirectPath } from '../utils/safeRedirect';
import { toast } from 'react-toastify';
import LogoLang from '../components/LogoLang';

export const action =
  (queryClient) =>
  async ({ request }) => {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);

    try {
      const response = await customFetch.post('/auth/login', data);

      // MFA aktiv: Session entsteht erst nach der Verifikation
      if (response.data.mfaRequired) {
        const params = data.redirectURL
          ? `?redirectURL=${encodeURIComponent(data.redirectURL)}`
          : '';
        return redirect(`/login/verify${params}`);
      }

      persistLoginSession(queryClient, response.data.tokenUser);
      return redirect(safeRedirectPath(data.redirectURL));
    } catch (error) {
      toast.error(error?.response?.data?.msg);
      return error;
    }
  };

const Login = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [queryParameters] = useSearchParams();
  const isSubmitting = navigation.state === 'submitting';
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true);
    try {
      const { data } = await customFetch.post('/auth/webauthn/login-options');
      const assertionResponse = await startAuthentication({
        optionsJSON: data.options,
      });
      const { data: loginData } = await customFetch.post(
        '/auth/webauthn/login',
        { challengeId: data.challengeId, assertionResponse }
      );
      persistLoginSession(queryClient, loginData.tokenUser);
      navigate(safeRedirectPath(queryParameters.get('redirectURL')));
    } catch (error) {
      // Abbruch durch den User nicht als Fehler anzeigen
      if (error?.name !== 'NotAllowedError' && error?.name !== 'AbortError') {
        toast.error(
          error?.response?.data?.msg || 'Passkey-Anmeldung fehlgeschlagen'
        );
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };
  return (
    <>
      <div className="h-full bg-gray-50" style={{ height: '100vh' }}>
        <div className="h-full">
          <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <Link to="/">
                <LogoLang />
              </Link>
              <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                Logge dich bei deinem Account ein
              </h2>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
              <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
                <Form className="space-y-6" method="POST">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Email
                    </label>
                    <div className="mt-2">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Passwort
                    </label>
                    <div className="mt-2">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 rounded-sm border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                      />
                      <label
                        htmlFor="remember-me"
                        className="ml-3 block text-sm leading-6 text-gray-900"
                      >
                        Angaben speichern
                      </label>
                    </div>

                    <div className="text-sm leading-6">
                      <Link
                        to="/forgot-password"
                        href="#"
                        className="font-semibold text-svisit-primary-600 hover:text-svisit-primary-500"
                      >
                        Passwort vergessen?
                      </Link>
                    </div>
                  </div>
                  <input
                    type="hidden"
                    name="redirectURL"
                    value={queryParameters.get('redirectURL') || ''}
                  />

                  <div>
                    <button
                      type="submit"
                      className="flex w-full justify-center rounded-md bg-svisit-primary-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Loggt ein...' : 'Einloggen'}
                    </button>
                  </div>
                </Form>
                <div className="relative mt-6">
                  <div
                    className="absolute inset-0 flex items-center"
                    aria-hidden="true"
                  >
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm font-medium leading-6">
                    <span className="bg-white px-6 text-gray-900">oder</span>
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={isPasskeyLoading}
                    className="flex w-full justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    {isPasskeyLoading
                      ? 'Wartet auf Passkey...'
                      : 'Mit Passkey anmelden'}
                  </button>
                </div>
                <Link to="/register">
                  <div className="mt-4">
                    <button
                      type="button"
                      className="flex w-full justify-center rounded-md bg-white px-3 px-3 py-1.5 text-sm font-semibold leading-6 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Registrieren
                    </button>
                  </div>
                </Link>
              </div>

              {/* <p className="mt-10 text-center text-sm text-gray-500">
                Noch kein Kunde?{' '}
                <a
                  href="#"
                  className="font-semibold leading-6 text-svisit-primary-600 hover:text-svisit-primary-500"
                >
                  Jetzt gratis testen!
                </a>
              </p> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
